import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Sparkles, Heart, Sun, Cloud, Star, Rainbow, BookOpen, Palette, Music,
  Trees, Users, Brain, Hand, Mic, Smile, ShieldCheck, Video, Salad, Bed,
  CheckCircle2, Phone, MessageCircle, MapPin, Clock, Calendar, ArrowRight,
  Stethoscope, Rocket, Microscope, Shield, Trophy, Briefcase, Award,
  Waves, Droplet, GraduationCap, BabyIcon, School, MousePointer2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import InquiryForm from "@/components/site/InquiryForm";
import AnimatedCounter from "@/components/site/AnimatedCounter";
import Manifesto from "@/components/site/Manifesto";
import Marquee from "@/components/site/Marquee";
import SmoothScroll, { scrollToSection } from "@/components/site/SmoothScroll";
import { Reveal, RevealGroup, RevealItem, MaskLines } from "@/components/site/Reveal";
import { CONTACT, buildWhatsAppLink } from "@/lib/contact";
import { useT } from "@/lib/i18n";

const IMG = {
  hero: "https://images.pexels.com/photos/8422262/pexels-photo-8422262.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  classroom: "https://images.pexels.com/photos/8088099/pexels-photo-8088099.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  art: "https://images.pexels.com/photos/19720248/pexels-photo-19720248.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  baby: "https://images.pexels.com/photos/3614116/pexels-photo-3614116.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  nap: "https://images.pexels.com/photos/3933069/pexels-photo-3933069.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  playground: "https://images.pexels.com/photos/16850890/pexels-photo-16850890.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  splash: "https://images.pexels.com/photos/10498603/pexels-photo-10498603.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  doctor: "https://images.pexels.com/photos/5094111/pexels-photo-5094111.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  astronaut: "https://images.pexels.com/photos/5560518/pexels-photo-5560518.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const EASE = [0.22, 1, 0.36, 1];
const scrollToId = (id) => scrollToSection(id);

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: EASE, delay: 0.5 + i * 0.1 },
  }),
};

// ------------- HERO -------------
const Hero = () => {
  const { t } = useT();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden bg-gradient-to-b from-sky-50 via-white to-white"
    >
      <div className="absolute inset-0 bg-dots opacity-40" />
      {/* Floating elements */}
      <motion.div
        className="absolute top-32 left-6 md:left-20 animate-float-slow opacity-80"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 0.8, scale: 1 }}
        transition={{ delay: 0.9, duration: 0.6, ease: EASE }}
      >
        <Rainbow className="h-12 w-12 text-pink-400" strokeWidth={2.4} />
      </motion.div>
      <motion.div
        className="absolute top-44 right-8 md:right-32 animate-float-medium opacity-80"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 0.8, scale: 1 }}
        transition={{ delay: 1.0, duration: 0.6, ease: EASE }}
      >
        <Cloud className="h-16 w-16 text-sky-300" strokeWidth={2} fill="#E0F2FE" />
      </motion.div>
      <motion.div
        className="absolute bottom-24 left-10 md:left-32 animate-float-fast opacity-90"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 0.9, scale: 1 }}
        transition={{ delay: 1.1, duration: 0.6, ease: EASE }}
      >
        <Star className="h-9 w-9 text-amber-400" strokeWidth={2.5} fill="#FCD34D" />
      </motion.div>
      <motion.div
        className="absolute bottom-40 right-6 md:right-16 animate-pulse-soft"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, duration: 0.6, ease: EASE }}
      >
        <Sun className="h-14 w-14 text-yellow-400" strokeWidth={2} fill="#FDE047" />
      </motion.div>

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-6 text-center lg:text-left">
          <motion.div variants={fadeUp} custom={-3} initial="hidden" animate="show">
            <Badge
              data-testid="hero-badge"
              className="bg-white border border-sky-200 text-ark-skyDeep font-display font-semibold rounded-full px-4 py-2 shadow-sm"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              {t("hero.badge")}
            </Badge>
          </motion.div>

          <h1 className="mt-6 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ark-ink leading-[1.05]">
            <MaskLines lines={[t("hero.line1"), t("hero.line2")]} delay={0.15} />
            <span className="relative inline-block mt-1">
              <MaskLines lines={[t("hero.line3")]} lineClassName="text-rainbow" delay={0.4} />
              <motion.svg
                className="absolute -bottom-2 left-0 w-full h-3"
                viewBox="0 0 200 12"
                fill="none"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ delay: 1.0, duration: 0.8, ease: EASE }}
              >
                <motion.path
                  d="M2 9C50 2 100 2 198 9"
                  stroke="#0EA5E9"
                  strokeWidth="4"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ delay: 1.0, duration: 0.8, ease: EASE }}
                />
              </motion.svg>
            </span>
          </h1>

          <motion.p
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate="show"
            className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl mx-auto lg:mx-0"
          >
            {t("hero.tagline")}
          </motion.p>

          <motion.div
            variants={fadeUp}
            custom={3}
            initial="hidden"
            animate="show"
            className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start"
          >
            <Button
              data-testid="hero-book-tour-btn"
              onClick={() => scrollToId("contact")}
              className="rounded-full h-14 px-7 bg-ark-sky hover:bg-ark-skyDeep text-white font-display font-semibold text-base shadow-ark-sky transition-transform hover:scale-[1.03] active:scale-95"
            >
              <Calendar className="h-5 w-5 mr-2" strokeWidth={2.5} />
              {t("hero.ctaTour")}
            </Button>
            <Button
              data-testid="hero-admissions-btn"
              onClick={() => scrollToId("programs")}
              variant="outline"
              className="rounded-full h-14 px-7 border-2 border-ark-teal text-ark-tealDeep hover:bg-ark-teal hover:text-white font-display font-semibold text-base transition-transform hover:scale-[1.03] active:scale-95"
            >
              {t("hero.ctaAdmissions")} <ArrowRight className="h-5 w-5 ml-2" strokeWidth={2.5} />
            </Button>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={4}
            initial="hidden"
            animate="show"
            className="mt-10 flex items-center justify-center lg:justify-start gap-6 text-sm"
          >
            <div className="flex items-center gap-2 text-slate-600">
              <ShieldCheck className="h-5 w-5 text-ark-teal" strokeWidth={2.4} />
              <span className="font-semibold">{t("hero.safeCampus")}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Heart className="h-5 w-5 text-pink-500" strokeWidth={2.4} fill="#F9A8D4" />
              <span className="font-semibold">{t("hero.caringTeachers")}</span>
            </div>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-6 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.9, ease: EASE }}
            className="relative rounded-[2.5rem] overflow-hidden shadow-2xl shadow-sky-900/20 border-4 border-white"
          >
            <motion.img
              src={IMG.hero}
              alt="Happy preschoolers learning at Little Ark"
              data-testid="hero-image"
              loading="eager"
              style={{ y: imgY, scale: imgScale }}
              className="w-full h-[420px] md:h-[520px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-sky-900/10 to-transparent" />
          </motion.div>

          {/* Floating badge cards */}
          <motion.div
            initial={{ opacity: 0, x: -20, y: -10 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7, ease: EASE }}
            className="absolute -top-4 -left-3 md:-left-8 bg-white rounded-3xl shadow-xl p-4 flex items-center gap-3 animate-float-slow"
          >
            <div className="h-12 w-12 rounded-2xl bg-amber-100 flex items-center justify-center">
              <Smile className="h-7 w-7 text-amber-500" strokeWidth={2.4} />
            </div>
            <div>
              <div className="font-display font-bold text-xl text-ark-ink">{t("hero.happyKids")}</div>
              <div className="text-xs text-slate-500">{t("hero.happyKidsSub")}</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20, y: 10 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ delay: 0.95, duration: 0.7, ease: EASE }}
            className="absolute -bottom-5 -right-3 md:-right-8 bg-white rounded-3xl shadow-xl p-4 flex items-center gap-3 animate-float-medium"
          >
            <div className="h-12 w-12 rounded-2xl bg-emerald-100 flex items-center justify-center">
              <BookOpen className="h-7 w-7 text-emerald-600" strokeWidth={2.4} />
            </div>
            <div>
              <div className="font-display font-bold text-xl text-ark-ink">{t("hero.activityBased")}</div>
              <div className="text-xs text-slate-500">{t("hero.activityBasedSub")}</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ------------- ABOUT -------------
const About = () => {
  const { t } = useT();
  const cards = [
    { icon: BookOpen, key: "learn", bg: "bg-sky-100", iconColor: "text-ark-skyDeep", span: "md:col-span-2" },
    { icon: Hand, key: "play", bg: "bg-pink-100", iconColor: "text-pink-500" },
    { icon: Trees, key: "explore", bg: "bg-emerald-100", iconColor: "text-emerald-600" },
    { icon: Heart, key: "grow", bg: "bg-amber-100", iconColor: "text-amber-500", span: "md:col-span-2" },
  ];
  return (
    <section id="about" data-testid="about-section" className="py-20 md:py-28 bg-white">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-3xl">
          <Badge className="bg-sky-100 text-ark-skyDeep border-0 rounded-full px-4 py-1.5 font-display font-semibold">
            {t("about.badge")}
          </Badge>
          <h2 className="mt-5 font-display font-bold text-3xl md:text-5xl text-ark-ink leading-tight">
            {t("about.headline")} <span className="text-ark-teal">{t("about.headlineAccent")}</span>
          </h2>
          <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed">
            {t("about.body")}
          </p>
        </div>

        <RevealGroup className="mt-12 grid md:grid-cols-3 gap-5">
          {cards.map((c, i) => (
            <RevealItem key={c.key} className={c.span || ""}>
              <Card
                data-testid={`about-card-${c.key}`}
                className="h-full card-lift border-0 rounded-4xl shadow-ark-soft hover:shadow-ark-sky"
              >
                <CardContent className="p-7">
                  <div className={`h-14 w-14 rounded-2xl ${c.bg} flex items-center justify-center`}>
                    <c.icon className={`h-7 w-7 ${c.iconColor}`} strokeWidth={2.5} />
                  </div>
                  <h3 className="mt-5 font-display font-bold text-2xl text-ark-ink">{t(`about.card.${c.key}`)}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed">{t(`about.card.${c.key}Desc`)}</p>
                </CardContent>
              </Card>
            </RevealItem>
          ))}
        </RevealGroup>
      </div>
    </section>
  );
};

// ------------- PROGRAMS -------------
const Programs = () => {
  const { t } = useT();
  const programs = [
    { key: "playgroup", age: "1.5 – 2.5 yrs", time: "9:30 AM – 12:00 PM", color: "bg-pink-100", icon: BabyIcon, accent: "text-pink-500" },
    { key: "nursery", age: "2.5 – 3.5 yrs", time: "9:00 AM – 12:30 PM", color: "bg-sky-100", icon: Smile, accent: "text-ark-skyDeep" },
    { key: "lkg", age: "3.5 – 4.5 yrs", time: "9:00 AM – 1:00 PM", color: "bg-amber-100", icon: BookOpen, accent: "text-amber-500" },
    { key: "ukg", age: "4.5 – 6 yrs", time: "9:00 AM – 1:30 PM", color: "bg-emerald-100", icon: GraduationCap, accent: "text-emerald-600" },
    { key: "daycare", age: "1.5 – 6 yrs", time: "9:00 AM – 6:00 PM", color: "bg-violet-100", icon: ShieldCheck, accent: "text-violet-600" },
  ];

  return (
    <section id="programs" data-testid="programs-section" className="py-20 md:py-28 bg-gradient-to-b from-white via-sky-50/40 to-white">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <Badge className="bg-amber-100 text-amber-700 border-0 rounded-full px-4 py-1.5 font-display font-semibold">
              {t("programs.badge")}
            </Badge>
            <h2 className="mt-5 font-display font-bold text-3xl md:text-5xl text-ark-ink leading-tight">
              {t("programs.headline")} <span className="text-ark-skyDeep">{t("programs.headlineAccent")}</span>
            </h2>
          </div>
          <p className="text-slate-600 max-w-md">
            {t("programs.subhead")}
          </p>
        </div>

        <RevealGroup data-testid="programs-grid" className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {programs.map((p) => (
            <RevealItem key={p.key} className="h-full">
              <Card
                data-testid={`program-card-${p.key}`}
                className="h-full card-lift border-0 rounded-4xl bg-white shadow-ark-soft overflow-hidden group"
              >
                <div className={`${p.color} p-7`}>
                  <div className="flex items-center justify-between">
                    <div className="h-14 w-14 rounded-2xl bg-white flex items-center justify-center shadow-sm">
                      <p.icon className={`h-7 w-7 ${p.accent}`} strokeWidth={2.5} />
                    </div>
                    <Badge className="bg-white/90 text-slate-700 border-0 rounded-full font-display font-semibold">
                      {p.age}
                    </Badge>
                  </div>
                  <h3 className="mt-5 font-display font-bold text-2xl text-ark-ink">{t(`programs.${p.key}`)}</h3>
                </div>
                <CardContent className="p-7">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Clock className="h-4 w-4 text-ark-teal" strokeWidth={2.4} />
                    <span className="font-semibold">{p.time}</span>
                  </div>
                  <p className="mt-3 text-slate-600 leading-relaxed">{t(`programs.${p.key}.focus`)}</p>
                  <button
                    data-testid={`program-enquire-${p.key}`}
                    onClick={() => scrollToId("contact")}
                    className="mt-5 inline-flex items-center gap-2 font-display font-semibold text-ark-skyDeep hover:text-ark-sky"
                  >
                    {t("programs.enquire")} {t(`programs.${p.key}`)} <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
                  </button>
                </CardContent>
              </Card>
            </RevealItem>
          ))}
        </RevealGroup>
      </div>
    </section>
  );
};

// ------------- CURRICULUM -------------
const Curriculum = () => {
  const { t } = useT();
  const items = [
    { icon: Hand, key: "handsOn", color: "bg-sky-100 text-ark-skyDeep" },
    { icon: BookOpen, key: "storytelling", color: "bg-pink-100 text-pink-500" },
    { icon: Brain, key: "phonics", color: "bg-amber-100 text-amber-500" },
    { icon: Sparkles, key: "sensory", color: "bg-violet-100 text-violet-500" },
    { icon: Trees, key: "outdoor", color: "bg-emerald-100 text-emerald-600" },
    { icon: Palette, key: "art", color: "bg-rose-100 text-rose-500" },
    { icon: Music, key: "music", color: "bg-indigo-100 text-indigo-500" },
    { icon: Mic, key: "communication", color: "bg-teal-100 text-ark-tealDeep" },
  ];
  return (
    <section id="curriculum" data-testid="curriculum-section" className="py-20 md:py-28 bg-white">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Badge className="bg-violet-100 text-violet-600 border-0 rounded-full px-4 py-1.5 font-display font-semibold">
              {t("curriculum.badge")}
            </Badge>
            <h2 className="mt-5 font-display font-bold text-3xl md:text-5xl text-ark-ink leading-tight">
              {t("curriculum.headline")}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              {t("curriculum.body")}
            </p>
            <div className="mt-6 rounded-4xl overflow-hidden shadow-ark-soft">
              <img
                src={IMG.art}
                alt="Art and craft activity"
                loading="lazy"
                className="w-full h-72 object-cover"
              />
            </div>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {items.map((it) => (
              <Card
                key={it.key}
                data-testid={`curriculum-${it.key}`}
                className="card-lift border-0 rounded-3xl shadow-ark-soft"
              >
                <CardContent className="p-5 flex items-center gap-4">
                  <div className={`h-12 w-12 rounded-2xl ${it.color} flex items-center justify-center shrink-0`}>
                    <it.icon className="h-6 w-6" strokeWidth={2.5} />
                  </div>
                  <span className="font-display font-semibold text-ark-ink text-base md:text-lg">{t(`curriculum.${it.key}`)}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ------------- DAY CARE -------------
const DayCare = () => {
  const { t } = useT();
  const features = [
    { icon: ShieldCheck, key: "safe" },
    { icon: Heart, key: "staff" },
    { icon: Video, key: "cctv" },
    { icon: Sparkles, key: "indoor" },
    { icon: Salad, key: "meals" },
    { icon: Bed, key: "nap" },
  ];
  return (
    <section id="daycare" data-testid="daycare-section" className="py-20 md:py-28 bg-gradient-to-br from-sky-50 to-teal-50/40">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <Badge className="bg-white text-ark-tealDeep border border-teal-200 rounded-full px-4 py-1.5 font-display font-semibold">
              {t("daycare.badge")}
            </Badge>
            <h2 className="mt-5 font-display font-bold text-3xl md:text-5xl text-ark-ink leading-tight">
              {t("daycare.headline")}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              {t("daycare.body")}
            </p>

            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {features.map((f) => (
                <li
                  key={f.key}
                  data-testid={`daycare-feature-${f.key}`}
                  className="flex items-center gap-3 bg-white rounded-2xl p-4 shadow-sm"
                >
                  <div className="h-10 w-10 rounded-xl bg-teal-100 flex items-center justify-center shrink-0">
                    <f.icon className="h-5 w-5 text-ark-tealDeep" strokeWidth={2.5} />
                  </div>
                  <span className="font-semibold text-slate-700 text-sm">{t(`daycare.feature.${f.key}`)}</span>
                </li>
              ))}
            </ul>

            <Button
              data-testid="daycare-cta-btn"
              onClick={() => scrollToId("contact")}
              className="mt-8 rounded-full h-12 px-6 bg-ark-teal hover:bg-ark-tealDeep text-white font-display font-semibold"
            >
              {t("daycare.cta")} <ArrowRight className="h-4 w-4 ml-2" strokeWidth={2.5} />
            </Button>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2 relative">
            <div className="rounded-[2.5rem] overflow-hidden shadow-ark-soft border-4 border-white">
              <img
                src={IMG.nap}
                alt="Cozy nap time at day care"
                loading="lazy"
                className="w-full h-[440px] object-cover"
              />
            </div>
            <div className="absolute -bottom-4 -left-3 bg-white rounded-3xl shadow-xl p-4 flex items-center gap-3 max-w-xs">
              <div className="h-12 w-12 rounded-2xl bg-pink-100 flex items-center justify-center shrink-0">
                <ShieldCheck className="h-7 w-7 text-pink-500" strokeWidth={2.4} />
              </div>
              <div>
                <div className="font-display font-bold text-lg text-ark-ink leading-tight">{t("daycare.safeBadge")}</div>
                <div className="text-xs text-slate-500">{t("daycare.safeBadgeSub")}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ------------- WHY CHOOSE -------------
const WhyChoose = () => {
  const { t } = useT();
  const cards = [
    { icon: Users, key: "small" },
    { icon: Heart, key: "caring" },
    { icon: ShieldCheck, key: "safe" },
    { icon: Palette, key: "creative" },
    { icon: Trees, key: "outdoor" },
    { icon: Award, key: "holistic" },
  ];
  const stats = [
    { value: 200, suffix: "+", key: "happyKids" },
    { value: 12, suffix: "+", key: "teachers" },
    { value: 5, suffix: "+", key: "years" },
    { value: 100, suffix: "%", key: "safe" },
  ];
  return (
    <section id="why" data-testid="why-section" className="py-20 md:py-28 bg-white">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <Badge className="bg-pink-100 text-pink-600 border-0 rounded-full px-4 py-1.5 font-display font-semibold">
            {t("why.badge")}
          </Badge>
          <h2 className="mt-5 font-display font-bold text-3xl md:text-5xl text-ark-ink leading-tight">
            {t("why.headline")} <span className="text-ark-skyDeep">{t("why.headlineAccent")}</span>
          </h2>
        </div>

        <div data-testid="why-counters" className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((c, i) => (
            <div
              key={c.key}
              className="rounded-3xl bg-gradient-to-br from-sky-50 to-teal-50 p-6 text-center shadow-ark-soft"
              data-testid={`stat-${c.key}`}
            >
              <div className="font-display font-bold text-4xl md:text-5xl text-ark-skyDeep">
                <AnimatedCounter end={c.value} suffix={c.suffix} testId={`counter-${i}`} />
              </div>
              <div className="mt-2 font-semibold text-slate-600 text-sm md:text-base">{t(`why.stat.${c.key}`)}</div>
            </div>
          ))}
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((c) => (
            <Card
              key={c.key}
              data-testid={`why-card-${c.key}`}
              className="card-lift border-0 rounded-4xl shadow-ark-soft"
            >
              <CardContent className="p-7">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-sky-100 to-teal-100 flex items-center justify-center">
                  <c.icon className="h-7 w-7 text-ark-tealDeep" strokeWidth={2.5} />
                </div>
                <h3 className="mt-5 font-display font-bold text-xl text-ark-ink">{t(`why.card.${c.key}`)}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{t(`why.card.${c.key}Desc`)}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// ------------- PLAYGROUND & SPLASH -------------
const Playground = () => {
  const { t } = useT();
  const items = [
    { key: "sandbox", icon: School, color: "bg-amber-100 text-amber-600" },
    { key: "splashWater", icon: Droplet, color: "bg-sky-100 text-ark-skyDeep" },
    { key: "slides", icon: Waves, color: "bg-pink-100 text-pink-500" },
    { key: "activity", icon: Sparkles, color: "bg-emerald-100 text-emerald-600" },
  ];
  return (
    <section id="playground" data-testid="playground-section" className="py-20 md:py-28 bg-white">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <Badge className="bg-sky-100 text-ark-skyDeep border-0 rounded-full px-4 py-1.5 font-display font-semibold">
            {t("playground.badge")}
          </Badge>
          <h2 className="mt-5 font-display font-bold text-3xl md:text-5xl text-ark-ink leading-tight">
            {t("playground.headline")}
          </h2>
          <p className="mt-5 text-slate-600 text-base md:text-lg">
            {t("playground.body")}
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-12 gap-5">
          <div className="md:col-span-7 rounded-4xl overflow-hidden shadow-ark-soft relative h-80 md:h-[460px]">
            <img src={IMG.playground} alt="Outdoor playground" loading="lazy" className="w-full h-full object-cover" />
            <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur rounded-2xl px-4 py-3">
              <div className="font-display font-bold text-lg text-ark-ink">{t("playground.openAir")}</div>
              <div className="text-xs text-slate-500">{t("playground.openAirSub")}</div>
            </div>
          </div>
          <div className="md:col-span-5 rounded-4xl overflow-hidden shadow-ark-soft relative h-80 md:h-[460px]">
            <img src={IMG.splash} alt="Splash water play" loading="lazy" className="w-full h-full object-cover" />
            <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur rounded-2xl px-4 py-3">
              <div className="font-display font-bold text-lg text-ark-ink">{t("playground.splash")}</div>
              <div className="text-xs text-slate-500">{t("playground.splashSub")}</div>
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {items.map((it) => (
            <Card
              key={it.key}
              data-testid={`playground-${it.key}`}
              className="card-lift border-0 rounded-3xl shadow-ark-soft"
            >
              <CardContent className="p-5 flex items-center gap-3">
                <div className={`h-11 w-11 rounded-2xl ${it.color} flex items-center justify-center shrink-0`}>
                  <it.icon className="h-5 w-5" strokeWidth={2.5} />
                </div>
                <span className="font-display font-semibold text-ark-ink text-sm md:text-base">{t(`playground.${it.key}`)}</span>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// ------------- GALLERY -------------
const Gallery = () => {
  const { t } = useT();
  const tiles = [
    { src: IMG.classroom, key: "classroom", className: "md:col-span-2 md:row-span-2" },
    { src: IMG.art, key: "art" },
    { src: IMG.playground, key: "outdoor" },
    { src: IMG.baby, key: "smiles" },
    { src: IMG.splash, key: "splash", className: "md:col-span-2" },
    { src: IMG.doctor, key: "dreams" },
  ];
  return (
    <section id="gallery" data-testid="gallery-section" className="py-20 md:py-28 bg-gradient-to-b from-white via-sky-50/40 to-white">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <Badge className="bg-amber-100 text-amber-700 border-0 rounded-full px-4 py-1.5 font-display font-semibold">
              {t("gallery.badge")}
            </Badge>
            <h2 className="mt-5 font-display font-bold text-3xl md:text-5xl text-ark-ink leading-tight">
              {t("gallery.headline")}
            </h2>
          </div>
          <p className="text-slate-600 max-w-md">
            {t("gallery.body")}
          </p>
        </div>

        <RevealGroup className="mt-12 grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-4" stagger={0.08}>
          {tiles.map((tile, i) => (
            <RevealItem
              key={i}
              className={tile.className || ""}
            >
              <div
                data-testid={`gallery-tile-${i}`}
                className="relative h-full w-full rounded-3xl overflow-hidden shadow-ark-soft group"
              >
                <img
                  src={tile.src}
                  alt={t(`gallery.${tile.key}`)}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur rounded-full px-3 py-1 text-xs font-display font-semibold text-ark-ink">
                  {t(`gallery.${tile.key}`)}
                </div>
              </div>
            </RevealItem>
          ))}
        </RevealGroup>
      </div>
    </section>
  );
};

// ------------- FUTURE DREAMS -------------
const FutureDreams = () => {
  const { t } = useT();
  const dreams = [
    { key: "doctor", icon: Stethoscope, color: "bg-rose-100 text-rose-500" },
    { key: "astronaut", icon: Rocket, color: "bg-violet-100 text-violet-500" },
    { key: "scientist", icon: Microscope, color: "bg-emerald-100 text-emerald-600" },
    { key: "police", icon: Shield, color: "bg-sky-100 text-ark-skyDeep" },
    { key: "athlete", icon: Trophy, color: "bg-amber-100 text-amber-500" },
    { key: "entrepreneur", icon: Briefcase, color: "bg-pink-100 text-pink-500" },
  ];
  return (
    <section data-testid="future-dreams-section" className="py-20 md:py-28 bg-gradient-to-br from-ark-sky to-ark-teal text-white relative overflow-hidden">
      <div className="absolute top-10 left-10 animate-float-slow opacity-30">
        <Star className="h-20 w-20 text-yellow-300" fill="#FDE047" />
      </div>
      <div className="absolute bottom-10 right-10 animate-float-medium opacity-30">
        <Rainbow className="h-24 w-24 text-pink-200" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <Badge className="bg-white/20 backdrop-blur text-white border-0 rounded-full px-4 py-1.5 font-display font-semibold">
            {t("future.badge")}
          </Badge>
          <h2 className="mt-5 font-display font-bold text-4xl md:text-6xl leading-[1.1]">
            {t("future.headline1")}<br />
            <span className="text-yellow-200">{t("future.headline2")}</span>
          </h2>
          <p className="mt-6 text-white/90 text-base md:text-lg leading-relaxed">
            {t("future.body")}
          </p>
        </div>

        <RevealGroup className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" stagger={0.07}>
          {dreams.map((d) => (
            <RevealItem key={d.key}>
              <div
                data-testid={`dream-${d.key}`}
                className="bg-white rounded-3xl p-5 text-center card-lift shadow-xl"
              >
                <div className={`mx-auto h-14 w-14 rounded-2xl ${d.color} flex items-center justify-center`}>
                  <d.icon className="h-7 w-7" strokeWidth={2.5} />
                </div>
                <div className="mt-3 font-display font-bold text-ark-ink">{t(`future.${d.key}`)}</div>
              </div>
            </RevealItem>
          ))}
        </RevealGroup>
      </div>
    </section>
  );
};

// ------------- TESTIMONIALS -------------
const TESTIMONIAL_KEYS = [
  { qKey: "testimonials.t1.quote", rKey: "testimonials.t1.role", initial: "P" },
  { qKey: "testimonials.t2.quote", rKey: "testimonials.t2.role", initial: "R" },
  { qKey: "testimonials.t3.quote", rKey: "testimonials.t3.role", initial: "A" },
  { qKey: "testimonials.t4.quote", rKey: "testimonials.t4.role", initial: "S" },
];

const Testimonials = () => {
  const { t } = useT();
  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-20 md:py-28 bg-white">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <Badge className="bg-emerald-100 text-emerald-700 border-0 rounded-full px-4 py-1.5 font-display font-semibold">
            {t("testimonials.badge")}
          </Badge>
          <h2 className="mt-5 font-display font-bold text-3xl md:text-5xl text-ark-ink leading-tight">
            {t("testimonials.headline")} <span className="text-ark-tealDeep">{t("testimonials.headlineAccent")}</span>
          </h2>
        </div>

        <Carousel
          opts={{ loop: true, align: "start" }}
          className="mt-12"
          data-testid="testimonials-carousel"
        >
          <CarouselContent className="-ml-4">
            {TESTIMONIAL_KEYS.map((tk, i) => (
              <CarouselItem
                key={i}
                className="pl-4 md:basis-1/2 lg:basis-1/3"
                data-testid={`testimonial-${i}`}
              >
                <Card className="border-0 rounded-4xl shadow-ark-soft h-full">
                  <CardContent className="p-7 flex flex-col h-full">
                    <div className="flex items-center gap-1 text-amber-400">
                      {Array.from({ length: 5 }).map((_, k) => (
                        <Star key={k} className="h-4 w-4" fill="#FBBF24" strokeWidth={0} />
                      ))}
                    </div>
                    <p className="mt-4 text-slate-700 leading-relaxed flex-1">&ldquo;{t(tk.qKey)}&rdquo;</p>
                    <div className="mt-6 flex items-center gap-3">
                      <div className="h-11 w-11 rounded-full bg-gradient-to-br from-ark-sky to-ark-teal flex items-center justify-center font-display font-bold text-white">
                        {tk.initial}
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">{t(tk.rKey)}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex justify-center gap-3 mt-8">
            <CarouselPrevious
              data-testid="testimonial-prev"
              className="static translate-x-0 translate-y-0 h-12 w-12 rounded-full border-ark-sky text-ark-skyDeep hover:bg-ark-sky hover:text-white"
            />
            <CarouselNext
              data-testid="testimonial-next"
              className="static translate-x-0 translate-y-0 h-12 w-12 rounded-full border-ark-sky text-ark-skyDeep hover:bg-ark-sky hover:text-white"
            />
          </div>
        </Carousel>
      </div>
    </section>
  );
};

// ------------- ADMISSION CTA + FORM -------------
const Admission = () => {
  const { t } = useT();
  return (
  <section id="contact" data-testid="admission-section" className="py-20 md:py-28 bg-gradient-to-b from-sky-50 to-white">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="text-center max-w-3xl mx-auto">
        <Badge className="bg-white text-ark-skyDeep border border-sky-200 rounded-full px-4 py-1.5 font-display font-semibold">
          {t("admission.badge")}
        </Badge>
        <h2 className="mt-5 font-display font-bold text-3xl md:text-5xl text-ark-ink leading-tight">
          {t("admission.headline")} <span className="text-rainbow">{t("admission.headlineAccent")}</span>
        </h2>
        <p className="mt-5 text-slate-600 text-base md:text-lg">
          {t("admission.body")}
        </p>
      </div>

      <div className="mt-12 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7">
          <InquiryForm />
        </div>
        <div className="lg:col-span-5 space-y-5">
          <Card className="border-0 rounded-4xl shadow-ark-soft overflow-hidden">
            <div className="aspect-video w-full">
              <iframe
                title="Little Ark Location"
                src={CONTACT.mapsEmbed}
                className="w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-ark-teal mt-1 shrink-0" />
                <div>
                  <div className="font-display font-bold text-ark-ink">{t("admission.visitUs")}</div>
                  <div className="text-sm text-slate-600">{CONTACT.address}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-ark-teal mt-1 shrink-0" />
                <div>
                  <div className="font-display font-bold text-ark-ink">{t("admission.schoolHours")}</div>
                  <div className="text-sm text-slate-600">{CONTACT.hours}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-4">
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              data-testid="contact-call-btn"
              className="rounded-3xl bg-ark-sky text-white p-5 shadow-ark-sky hover:bg-ark-skyDeep transition flex items-center gap-3"
            >
              <Phone className="h-6 w-6" strokeWidth={2.5} />
              <div>
                <div className="font-display font-bold">{t("admission.callNow")}</div>
                <div className="text-xs text-white/90">{CONTACT.phone}</div>
              </div>
            </a>
            <a
              href={buildWhatsAppLink("Hi! I'd like to enquire about admissions at Little Ark.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              className="rounded-3xl bg-green-500 text-white p-5 shadow-lg hover:bg-green-600 transition flex items-center gap-3"
            >
              <MessageCircle className="h-6 w-6" strokeWidth={2.5} />
              <div>
                <div className="font-display font-bold">{t("admission.whatsapp")}</div>
                <div className="text-xs text-white/90">{t("admission.whatsappSub")}</div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};

// ------------- PAGE -------------
const Home = () => {
  const { t, lang } = useT();
  useEffect(() => {
    // Bilingual SEO title & meta description — updates when language toggles.
    const title =
      lang === "kn"
        ? "ಲಿಟಲ್ ಆರ್ಕ್ ಪ್ರಿಸ್ಕೂಲ್ ಹಾರೋಹಳ್ಳಿ | ಪ್ಲೇ ಸ್ಕೂಲ್, ನರ್ಸರಿ, LKG, UKG ಮತ್ತು ಡೇ ಕೇರ್"
        : "Little Ark Preschool Harohalli | Best Play School, Nursery, LKG, UKG & Day Care";
    const description =
      lang === "kn"
        ? "ಹಾರೋಹಳ್ಳಿ, ಕನಕಪುರ ರಸ್ತೆಯಲ್ಲಿನ ಲಿಟಲ್ ಆರ್ಕ್ ಪ್ರಿಸ್ಕೂಲ್ 1.5–6 ವಯಸ್ಸಿನ ಮಕ್ಕಳಿಗಾಗಿ ಪ್ಲೇಗ್ರೂಪ್, ನರ್ಸರಿ, LKG, UKG ಮತ್ತು ಪೂರ್ಣ ದಿನ ಡೇ ಕೇರ್ ನೀಡುತ್ತದೆ. ಸಣ್ಣ ಬ್ಯಾಚ್‌ಗಳು, ಸಿಸಿಟಿವಿ ಸುರಕ್ಷಿತ ಕ್ಯಾಂಪಸ್, ಚಟುವಟಿಕೆ ಆಧಾರಿತ ಕಲಿಕೆ."
        : "Little Ark Preschool in Harohalli, Kanakapura Road offers Playgroup, Nursery, LKG, UKG and full day care for ages 1.5–6. Small classes, safe campus, activity-based learning, splash play & CCTV. Book a school tour today.";

    document.title = title;

    const ensureMeta = (name, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      return el;
    };
    ensureMeta("description").setAttribute("content", description);
    ensureMeta("og:title", "property").setAttribute("content", title);
    ensureMeta("og:description", "property").setAttribute("content", description);
    ensureMeta("twitter:title").setAttribute("content", title);
    ensureMeta("twitter:description").setAttribute("content", description);
  }, [lang]);

  return (
    <SmoothScroll>
      <div data-testid="home-page" className="App overflow-x-hidden">
        <Navbar />
        <main>
          <Hero />
          <Marquee
            items={[
              t("marquee.learn"),
              t("marquee.play"),
              t("marquee.explore"),
              t("marquee.grow"),
              t("marquee.imagine"),
              t("marquee.create"),
              t("marquee.dream"),
            ]}
            className="py-10 md:py-14 bg-gradient-to-r from-sky-50 via-white to-teal-50 text-ark-ink border-y border-sky-100"
          />
          <About />
          <Programs />
          <Curriculum />
          <Manifesto />
          <DayCare />
          <WhyChoose />
          <Playground />
          <Gallery />
          <FutureDreams />
          <Testimonials />
          <Admission />
        </main>
        <Footer />
        <WhatsAppFloat />
      </div>
    </SmoothScroll>
  );
};

export default Home;
